import { type ReactNode } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import { compact, dayTick, num, pct } from "../lib/format";

// ═══════════════════════════════════════════════════════════════════════════
//  CHARTS
// ═══════════════════════════════════════════════════════════════════════════
// Four forms, chosen by what the reader has to do:
//
//   TrendChart   change over time, 2–4 series      → line, categorical hues
//   BarList      compare magnitude across names    → bars, ONE hue
//   Funnel       ordered stages, where the drop is → bars, ONE hue
//   Heatmap      magnitude across a grid           → sequential ramp
//
// Rules that hold across all four, and that are easy to undo by accident:
//
//   · Never two y-axes. Two measures of different scale get two charts. A
//     dual axis invents a correlation the data does not contain, and it is
//     the single most common way a dashboard misleads.
//   · Colour follows the ENTITY, never its rank. Filtering a series out must
//     not repaint the survivors — a reader who learned "renders are orange"
//     is misled the moment orange becomes something else.
//   · One hue for a ranked bar list. Colouring bars darker-where-bigger
//     double-encodes the length as hue and burns the only free channel on
//     information the bar already carries.
//
// The exception, and it is a real one: on the colour panels the swatch beside
// each row IS the data — a paint shade. That is identity, not decoration, so
// the swatch carries the hue and the BAR stays in the single series colour.
// Painting the bars in the paint's own colour would be prettier and useless:
// half this fandeck is off-white, and an off-white bar on an off-white card
// cannot be read at all.
//
// ── Why every colour here is a `var(...)` string, never a resolved hex ─────
// The obvious version of this file read the tokens with getComputedStyle at
// render time. It looks fine and is wrong: the theme toggle sets a data-theme
// attribute in an effect, which runs AFTER the render that reacted to it, so
// every chart kept the previous mode's palette until something else happened
// to re-render it. Handing `var(--series-1)` straight to the SVG presentation
// attribute makes the browser resolve it on every repaint, which is exactly
// the behaviour wanted and is one fewer thing to keep in sync.

const SERIES = [
  "var(--series-1)",
  "var(--series-2)",
  "var(--series-3)",
  "var(--series-4)",
];

export interface SeriesSpec {
  key: string;
  label: string;
}

// ── Tooltip ────────────────────────────────────────────────────────────────

function ChartTooltip({
  active,
  payload,
  label,
  labelFormat,
}: TooltipProps<number, string> & { labelFormat?: (v: string) => string }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="tooltip">
      <div className="tooltip-title">
        {labelFormat ? labelFormat(String(label)) : String(label)}
      </div>
      {payload.map((entry) => (
        <div className="tooltip-row" key={String(entry.dataKey)}>
          <span className="dot" style={{ background: entry.color }} />
          <span>{entry.name}</span>
          <span className="val">{num(Number(entry.value))}</span>
        </div>
      ))}
    </div>
  );
}

// ── Trend ──────────────────────────────────────────────────────────────────

/**
 * Multi-series line over the day buckets.
 *
 * A legend is always present, and with four series it is mandatory rather
 * than optional — identity must never rest on colour alone.
 */
export function TrendChart<T extends Record<string, unknown>>({
  data,
  series,
  height = 260,
}: {
  data: T[];
  series: SeriesSpec[];
  height?: number;
}) {
  return (
    <>
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <LineChart
            data={data}
            margin={{ top: 6, right: 10, bottom: 0, left: -18 }}
          >
            {/* Solid hairlines, horizontal only. Dashed grids read as a
                threshold or a projection when they are just a grid. */}
            <CartesianGrid
              stroke="var(--grid)"
              strokeWidth={1}
              vertical={false}
            />
            <XAxis
              dataKey="day"
              tickFormatter={dayTick}
              tickLine={false}
              axisLine={{ stroke: "var(--axis)" }}
              minTickGap={28}
            />
            <YAxis
              tickFormatter={(v: number) => compact(v)}
              tickLine={false}
              axisLine={false}
              width={48}
              allowDecimals={false}
            />
            <Tooltip
              content={<ChartTooltip labelFormat={dayTick} />}
              cursor={{ stroke: "var(--axis)", strokeWidth: 1 }}
            />
            {series.map((s, i) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={SERIES[i % SERIES.length]}
                strokeWidth={2}
                dot={false}
                // A 2px surface ring, so an active dot sitting on a gridline
                // or another series still reads as one mark.
                activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--surface)" }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="legend">
        {series.map((s, i) => (
          <span className="legend-item" key={s.key}>
            <span
              className="dot"
              style={{ background: SERIES[i % SERIES.length] }}
            />
            {s.label}
          </span>
        ))}
      </div>
    </>
  );
}

// ── Ranked bars ────────────────────────────────────────────────────────────

export interface BarItem {
  key: string;
  label: ReactNode;
  value: number;
  /** A paint swatch, where the row's identity IS a colour. */
  swatch?: string;
  /** Shown to the right of the value — a count, a share, a second measure. */
  meta?: ReactNode;
}

export function BarList({
  items,
  total,
  valueFormat = num,
}: {
  items: BarItem[];
  /** Scale denominator. Defaults to the largest value, not the sum. */
  total?: number;
  valueFormat?: (value: number) => string;
}) {
  const max = total ?? Math.max(1, ...items.map((i) => i.value));

  return (
    <div className="barlist">
      {items.map((item) => (
        <div className="barlist-row" key={item.key}>
          <div className="barlist-label">
            {item.swatch && (
              <span
                className="swatch swatch-sm"
                style={{ background: item.swatch }}
                title={item.swatch}
              />
            )}
            <span className="barlist-name">{item.label}</span>
          </div>
          <div className="barlist-track">
            <div
              className="barlist-fill"
              style={{ width: `${Math.max(1, (item.value / max) * 100)}%` }}
            />
          </div>
          <div className="barlist-value">
            {valueFormat(item.value)}
            {item.meta && (
              <span
                className="cell-mute"
                style={{ marginLeft: 8, fontWeight: 500 }}
              >
                {item.meta}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Funnel ─────────────────────────────────────────────────────────────────

/**
 * Ordered stages, one hue.
 *
 * The stages already read as ordered — stacked top to bottom and labelled — so
 * a light-to-dark ramp across them would add nothing the layout does not
 * already say. What the reader actually needs is the DROP between consecutive
 * stages, which is printed between the bars rather than left to be worked out
 * by comparing two lengths.
 *
 * The count sits OUTSIDE the bar, always. Inside would be prettier on the
 * wide stages and unreadable on the narrow ones — a label clipped by its own
 * bar, or white text on a mid-blue at 3.6:1, and the last stage of a funnel is
 * always the narrow one.
 */
export function Funnel({
  stages,
}: {
  stages: Array<{
    key: string;
    label: string;
    users: number;
    ofTotal: number;
    ofPrevious: number;
  }>;
}) {
  const max = Math.max(1, ...stages.map((s) => s.users));

  return (
    <div className="funnel">
      {stages.map((stage, i) => (
        <div key={stage.key}>
          {i > 0 && (
            <div className="funnel-drop">
              {stage.ofPrevious >= 99.95
                ? "no drop-off"
                : `${pct(100 - stage.ofPrevious, 0)} drop from previous step`}
            </div>
          )}
          <div className="funnel-stage">
            <span className="cell-mute" style={{ color: "var(--text-2)" }}>
              {stage.label}
            </span>
            <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <div
                className="funnel-bar"
                style={{ width: `${Math.max(0.5, (stage.users / max) * 100)}%` }}
              />
              <span className="funnel-outside">{num(stage.users)}</span>
              <span
                className="cell-mute"
                style={{ marginLeft: 8, whiteSpace: "nowrap" }}
              >
                {pct(stage.ofTotal, 0)} of signups
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Heatmap ────────────────────────────────────────────────────────────────

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SEQ = [
  "var(--seq-1)",
  "var(--seq-2)",
  "var(--seq-3)",
  "var(--seq-4)",
  "var(--seq-5)",
  "var(--seq-6)",
  "var(--seq-7)",
];

/**
 * Renders by local weekday and hour. Sequential single hue, light → dark.
 *
 * Every cell carries a `title`, so a value is reachable without landing on a
 * 14px target precisely — a tooltip must never be the only route to a number.
 */
export function Heatmap({
  grid,
  peak,
  timezone,
}: {
  grid: number[][];
  peak: number;
  timezone: string;
}) {
  function colorFor(value: number): string {
    if (value <= 0 || peak <= 0) return "var(--surface-2)";
    // Square-rooted so one very busy hour does not flatten the rest of the
    // grid into the lightest step. Peak hours are spiky by nature.
    const t = Math.sqrt(value / peak);
    return SEQ[Math.min(SEQ.length - 1, Math.floor(t * SEQ.length))];
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <div className="heatmap">
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span className="heat-hour" key={`h${h}`}>
            {h % 3 === 0 ? h : ""}
          </span>
        ))}

        {grid.map((row, day) => (
          <Row key={day} day={day} row={row} colorFor={colorFor} />
        ))}
      </div>

      <div className="heat-legend">
        <span>Fewer</span>
        {SEQ.map((c, i) => (
          <span
            className="heat-legend-swatch"
            key={i}
            style={{ background: c }}
          />
        ))}
        <span>More renders</span>
        <span style={{ marginLeft: "auto" }}>
          Local hours ({timezone}) · peak {num(peak)}
        </span>
      </div>
    </div>
  );
}

function Row({
  day,
  row,
  colorFor,
}: {
  day: number;
  row: number[];
  colorFor: (value: number) => string;
}) {
  return (
    <>
      <span className="heat-axis">{DAYS[day]}</span>
      {row.map((value, hour) => (
        <span
          className="heat-cell"
          key={hour}
          style={{ background: colorFor(value) }}
          title={`${DAYS[day]} ${String(hour).padStart(2, "0")}:00 — ${num(value)} render${value === 1 ? "" : "s"}`}
        />
      ))}
    </>
  );
}
